import { useState, useEffect, lazy, Suspense, useTransition } from 'react'
import { PersistGate } from 'redux-persist/integration/react'
import { getPersistor } from '@rematch/persist'
import { Route, Routes, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Provider } from 'react-redux'
import UserLayout from './components/layout/User'
import LoginLayout from './components/layout/Login'
import ProtectedRoute from './components/routes/Protected'
import { store } from './redux/store'
import { ConfigProvider, Spin } from 'antd'
import './assets/styles/main.css'
import './assets/styles/responsive.css'
import { extractIdToken, getUserRoleFromToken, setRouterHistory } from './utils'
import { resetSessionExpiry } from './utils/tokenRefresh'
import { useCallback } from 'react'
import { initSecurity } from './utils/security'
import WizrFlexLayout from './components/WizrFlexLayout'
import { WizrModalProvider } from './providers/modal/WizrModalProvider'
import { WizrNotificationProvider } from './providers/notification/NotificationProvider'
import { ReactFlowProvider } from 'reactflow'
import jwt_decode from 'jwt-decode'
import { dispatchSetPermissionValues } from './redux/actions/content'
import { dispatchNotificationState } from './redux/actions/common'
import { AuthProvider } from './authContext'
import ExtensionRedirectScreen from './pages/ExtensionRedirectScreen'
import Scheduler from './pages/Scheduler'
import SharePointPicker from './pages/components/SharePointPicker'
import AutoSolveTestHistory from './pages/components/CXSettingsComponents/AutoSolveComponents.tsx/TestAreaComponents.tsx/AutosolveTestHistory'
import AutoSolveEvalsHistory from './pages/components/CXSettingsComponents/AutoSolveComponents.tsx/TestAreaComponents.tsx/AutosolveEvalsHistory'
import DetailedLogs from './pages/components/CXSettingsComponents/AutoSolveComponents.tsx/TestAreaComponents.tsx/DetailedLogs'
import CustomerIntentsAndSentiments from './pages/components/CXControlHubComponents/CustomerIntentsAndSentiments'
import SdlcRedirectScreen from './pages/WizrsdlcRedirectScreen'
import SessionExpiredModal from './components/SessionExpiredModal'
// import SharePointPicker from './pages/components/SharePointPicker'

// {
//   components: {
//     Button: {
//        colorPrimaryBorderHover: 'red',
//        colorPrimaryHover: 'lightgray',
//        colorPrimary: 'red',
//        colorPrimaryActive: 'lightgray',
//        colorPrimaryTextHover: 'lightgray',
//     }
//   }
// }

const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
//const DashboardPage = lazy(() => import('./pages/Dashboard'))
const DashboardPage = lazy(() => import('./pages/HomeDashboard'))
const UsageInsightsDashboard = lazy(() => import('./pages/Dashboard'))
const Folders = lazy(() => import('./pages/Folders'))
const Files = lazy(() => import('./pages/Files'))
const Prompt = lazy(() => import('./pages/PromptList'))
const PromptView = lazy(() => import('./pages/PromptView'))
const Settings = lazy(() => import('./pages/Settings'))
const AppDesigner = lazy(() => import('./pages/AppDesigner'))
const AppDesignerTool = lazy(() => import('./pages/AppDesignerTool'))
const History = lazy(() => import('./pages/PromptHistory'))
const AppHistory = lazy(() => import('./pages/AppHistory'))
const AppEvalsHistory = lazy(() => import('./pages/AppEvalsHistory'))
const AppShare = lazy(() => import('./pages/AppShare'))
const BulkRun = lazy(() => import('./pages/BulkRun'))
const DocView = lazy(() => import('./pages/DocView'))
const AppInsights = lazy(() => import('./pages/AppInsights'))
const RunHistory = lazy(() => import('./pages/RunHistory'))
const EnterpriseContent = lazy(() => import('./pages/EnterpriseContent'))
const EndUserAppListing = lazy(() => import('./pages/EndUserAppListing'))
const EndUserHistory = lazy(() => import('./pages/EndUserHistory'))
const BulkRunList = lazy(() => import('./pages/BulkRunsList'))
const TenantDetails = lazy(() => import('./pages/components/SettingsComponents/TenantDetails'))
const UserDetail = lazy(() => import('./pages/components/SettingsComponents/UserDetail'))
const CxSettings = lazy(() => import('./pages/CxSettings'))
const PastTickets = lazy(() => import('./pages/PastTickets'))
const Faq = lazy(() => import('./pages/FAQ'))
const GroupListing = lazy(() => import('./pages/components/CXSettingsComponents/GroupListing'))
const DataListingGroup = lazy(() => import('./pages/components/CXSettingsComponents/DataListingGroup'))
const NewFeatureScreen = lazy(() => import('./pages/NewFeatureScreen'))
const Company = lazy(() => import('./pages/components/CXSettingsComponents/Company'))
const FeatureTest = lazy(() => import('./pages/components/CXSettingsComponents/FeatureTest'))
const Tickets = lazy(() => import('./pages/tickets'))
const EndPoint = lazy(() => import('./pages/components/CXSettingsComponents/EndPoint'))
const AutoTag = lazy(() => import('./pages/components/CXSettingsComponents/AutoTag'))
const AgentAssist = lazy(() => import('./pages/components/CXSettingsComponents/AgentAssist'))
const ControlRoom = lazy(() => import('./pages/CxControlRoom'))
const CreateRoles = lazy(() => import('./pages/components/SettingsComponents/CreateRoles'))
const UpdateProduct = lazy(() => import('./pages/components/SettingsComponents/UpdateProduct'))
// const ExtensionRedirectScreen = lazy(() => import('./pages/ExtensionRedirectScreen'))
const ChatBot = lazy(() => import('./pages/ChatBot'))
const ChatBotListing = lazy(() => import('./pages/ChatBotListing'))
const AutoSolve = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolve'))
const ConfigureAutosolve = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/ConfigureAutosolve'))
const WorkflowConfigure = lazy(() => import('./pages/WorkflowConfigure'))
const AutoSolveChatBot = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/AutoSolveChatBot'))
const WidgetCreation = lazy(() => import('./pages/components/CXSettingsComponents/Widgets'))
const CreateAgent = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/CreateAgent'))
const WizrNoAccessScreen = lazy(() => import('./pages/errorPage'))
const CustomerInteractions = lazy(() => import('./pages/CustomerInteractionsClient'))
const AIAgentInsights = lazy(() => import('./pages/AIAgentInsights'))
const AgentAssistInsights = lazy(() => import('./pages/AgentAssistInsights'))
const SeekerInsights = lazy(() => import('./pages/SeekerInsights'))
const CustomerInteractionsAgentAssist = lazy(() => import('./pages/CustomerInteractions'))
// const CustomerInteractionsClient = lazy(() => import('./pages/CustomerInteractionsClient'))
// const WebFormatLargePreview = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/WebformatlargePreviewComponents/WebFormatLargePreview'))
const TestEmbed = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/WebChatLargePreviewScreen'))
//const AgentAssist2 = lazy(()=>import('./pages/components/CXSettingsComponents/AgentAssist2'))
const HelpAndSupport = lazy(() => import('./pages/HelpAndSupport'))
const AppInsightsWhole = lazy(() => import('./pages/components/AppInsightsWholes'))
const AgentTag = lazy(() => import('./pages/components/CXSettingsComponents/AgentTag'))
const WidgetHistory = lazy(() => import('./pages/WidgetHistory'))
const WebChatSmallPreview = lazy(() => import('./pages/components/CXSettingsComponents/AutoSolveComponents.tsx/WebChatSmallPreviewScreen'))
const SchedulerHistory = lazy(() => import('./pages/SchedulerHistory'));
const EnterpriseHub = lazy(() => import('./pages/EnterpriseHub'));
const LiveAgent = lazy(() => import('./pages/LiveAgent'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Notifications = lazy(() => import('./pages/AlertsAndReports'));
const NewAutoSolve = lazy(() => import('./pages/AutoSolveListPage'));
const NewAutoTag = lazy(() => import('./pages/AutoTaggingPage'))
const NewToolsPage = lazy(() => import('./pages/NewToolsPage'));
const NewIntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const NewAgentAssist = lazy(() => import('./pages/AgentAssistNewPage'));
const LangfuseRedirectPage = lazy(() => import('./pages/LangfuseRedirectPage'));
import TrustforteList from './pages/TrustforteList'
const TempEditor = lazy(() => import('./pages/TempEditor'))

const wizrTheme = {
  components: {
    Switch: {
      colorPrimary: '#22C55E',
    },
    Button: {
      // colorPrimary: 'background: var(--grayscale-800, #000000)',
      colorPrimaryHover: '#5D5F62',
    },
  },
  token: {
    colorPrimary: 'background: var(--grayscale-800, #1D1F22)',
  },
}

interface ITokenDecoded {
  isForgotPassword: boolean
}
const App = (): JSX.Element => {
  // Don't initialize as authenticated if OAuth flow is in progress (code in URL or pkce pending)
  const searchParams = new URLSearchParams(window.location.search)
  const isOAuthInProgress = searchParams.get('code') || sessionStorage.getItem('pkce_in_progress')

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !isOAuthInProgress && Boolean(localStorage.getItem('id_token')),
  )
  const [isUserLayout, setIsUserLayout] = useState<boolean>(
    !isOAuthInProgress && Boolean(localStorage.getItem('id_token')),
  )

  const location = useLocation()

  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition()

  // Initialize security measures
  useEffect(() => {
    initSecurity();
  }, []);

  // Helper function to handle extension token processing
  const handleExtensionToken = (idToken: string, isForgotPassword: boolean) => {
    if (isForgotPassword) {
      navigate('/extension/login')
      setIsUserLayout(true)
    } else {
      localStorage.setItem('extension_id_token', idToken)
      navigate('/extension')
      setIsUserLayout(true)
    }
  }

  // Helper function to handle regular token processing
  const handleRegularToken = (idToken: string, userDetails: string, tokenDecoded: any) => {
    // If coming from a password reset flow, force a clean re-auth
    if (localStorage.getItem('reset') === 'true') {
      localStorage.removeItem('id_token')
      localStorage.removeItem('extension_id_token')
      localStorage.removeItem('sdlc_id_token')
      localStorage.removeItem('superset_access_token')
      localStorage.removeItem('superset_csrf_token')
      localStorage.removeItem('superset_last_auth_time')
      localStorage.setItem('reset', 'false')
      navigate('/login')
      return
    }
    localStorage.setItem('id_token', idToken)
    sessionStorage.removeItem('pkce_in_progress')
    
    // Reset session expiry flag on successful login
    resetSessionExpiry()
    
    if (idToken) {
      dispatchSetPermissionValues(idToken)
    }
    setIsAuthenticated(Boolean(localStorage.getItem('id_token')))
    setIsUserLayout(true)

    if (userDetails === 'test_enduser') {
      navigate('/euserapplisting')
    } else if (localStorage.getItem('reset') !== 'true') {
      if (tokenDecoded?.extension_UserRole === 'agent' || tokenDecoded?.extension_UserRole === 'liveagent') {
        navigate('/liveagent')
      } else {
        navigate('/home')
      }
    } else {
      navigate('/login')
    }
  }

  // Helper function to handle SDLC token processing
  const handleSdlcToken = (idToken: string, isForgotPassword: boolean) => {
    if (isForgotPassword) {
      navigate('/wizrsdlc/login')
      setIsUserLayout(true)
    } else {
      localStorage.setItem('sdlc_id_token', idToken)
      navigate('/wizrsdlc')
      setIsUserLayout(true)
    }
  }

  // Exchange authorization code for id_token + refresh_token via PKCE
  const exchangeCodeForTokens = useCallback(async (code: string) => {
    sessionStorage.setItem('pkce_in_progress', '1')

    const verifier = localStorage.getItem('pkce_code_verifier')
    if (!verifier) {
      console.error('PKCE verifier missing from localStorage — cannot exchange code')
      sessionStorage.removeItem('pkce_in_progress')
      navigate('/login')
      return
    }

    const tokenEndpoint = `https://wizrai.b2clogin.com/wizrai.onmicrosoft.com/oauth2/v2.0/token?p=${process.env.REACT_APP_WIZR_B2C_P}`

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: process.env.REACT_APP_WIZR_B2C_CLIENT_ID || '',
      redirect_uri: process.env.REACT_APP_WIZR_B2C_REDIRECT_URI || '',
      code,
      code_verifier: verifier,
      scope: 'openid offline_access',
    })

    try {
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })

      if (!response.ok) {
        console.error('Token exchange failed:', await response.text())
        sessionStorage.removeItem('pkce_in_progress')
        navigate('/login')
        return
      }

      const tokens = await response.json()
      let idToken: string = tokens.id_token
      const refreshToken: string = tokens.refresh_token

      localStorage.removeItem('pkce_code_verifier')

      // Store tokens first so getValidToken can access them
      localStorage.setItem('id_token', idToken)
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken)

      // Reset session expiry flag on successful login
      resetSessionExpiry()

      // Import getValidToken dynamically
      const { getValidToken } = await import('./utils/tokenRefresh')

      // Validate and refresh token if needed BEFORE proceeding
      const validToken = await getValidToken()
      if (validToken) {
        idToken = validToken
      }

      const userDetails = getUserRoleFromToken(idToken)
      const tokenDecoded: any = jwt_decode(idToken)

      // Clean the ?code= param from the URL without triggering a reload
      window.history.replaceState({}, document.title, window.location.pathname)

      startTransition(() => {
        if (tokenDecoded?.isForgotPassword) {
          navigate('/login')
        } else {
          handleRegularToken(idToken, userDetails, tokenDecoded)
        }
      })
    } catch (err) {
      console.error('Token exchange error:', err)
      sessionStorage.removeItem('pkce_in_progress')
      navigate('/login')
    }
  }, [navigate])

  // Helper function to process token and determine navigation
  const processTokenAndNavigate = async (location: any) => {
    if (!location?.hash?.includes('id_token')) return

    let idToken = extractIdToken(location.hash)
    const userDetails = getUserRoleFromToken(idToken)
    let isForgotPassword = false
    const tokenDecoded: any = jwt_decode<ITokenDecoded>(idToken)

    if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {
      isForgotPassword = tokenDecoded.isForgotPassword
    }

    // For regular tokens (not extension/sdlc), validate and refresh if needed
    if (!location?.pathname?.includes('extension') && !location?.pathname?.includes('wizrsdlc') && !isForgotPassword) {
      // Store token first so getValidToken can access it
      localStorage.setItem('id_token', idToken)

      // Reset session expiry flag on successful login
      resetSessionExpiry()

      // Import and validate token
      const { getValidToken } = await import('./utils/tokenRefresh')
      const validToken = await getValidToken()
      if (validToken) {
        idToken = validToken
      }
    }

    startTransition(() => {
      if (location?.pathname?.includes('extension')) {
        handleExtensionToken(idToken, isForgotPassword)
      } else if (location?.pathname?.includes('wizrsdlc')) {
        handleSdlcToken(idToken, isForgotPassword)
      } else if (isForgotPassword) {
        navigate('/login')
      } else {
        handleRegularToken(idToken, userDetails, tokenDecoded)
      }
    })
  }

  const FrameBuster: React.FC = () => {
    useEffect(() => {
      if (window.top && window.top !== window.self) {
        navigate('/login');
      }
    }, []);

    return null;
  };

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#error')) {
        // Parse the error information
        const params = new URLSearchParams(hash.slice(1));
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        console.log('Auth error detected:', error, errorDescription);

        // Handle all authentication errors the same way
        // Clear any existing tokens
        localStorage.removeItem('id_token');
        localStorage.removeItem('extension_id_token');

        // Reset authentication state
        setIsAuthenticated(false);
        setIsUserLayout(false);

        // Redirect to login page
        navigate('/login');
      }
    };

    handleHash();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHash);

    return () => {
      window.removeEventListener('hashchange', handleHash);
    };
  }, [navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const code = searchParams.get('code')
    if (code) {
      // During OAuth flow, don't show authenticated UI
      setIsAuthenticated(false)
      setIsUserLayout(false)
      exchangeCodeForTokens(code)
      return
    }
    processTokenAndNavigate(location)
  }, [location])

  setRouterHistory({ navigate, location })

  const AppLayout = isUserLayout ? UserLayout : LoginLayout

  useEffect(() => {
    // If authenticated and on root, navigate to home
    if (isAuthenticated && (location.pathname === '/' || location.pathname === '')) {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(localStorage.getItem('id_token') || '')
      if (tokenDecoded?.extension_UserRole === 'agent' || tokenDecoded?.extension_UserRole === 'liveagent') {
        navigate('/liveagent')
      }
      else {
        navigate('/home')
      }
    }
  }, [isAuthenticated, location.pathname, navigate]);

  return (
    <WizrFlexLayout>
      <ConfigProvider theme={wizrTheme}>
        <Provider store={store}>
          <AuthProvider>
            <PersistGate loading={<Spin />} persistor={getPersistor()}>
              <WizrModalProvider>
                <WizrNotificationProvider>
                  <ReactFlowProvider>
                    <FrameBuster />
                    <SessionExpiredModal />

                    <Suspense fallback={<UserLayout >
                      <WizrFlexLayout justifyContent='center'>
                        {/* <Skeleton active /> */}
                        <Spin />
                      </WizrFlexLayout>

                    </UserLayout>}>
                      {isPending ? (
                        // <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        //   <Spin size="large" />
                        // </div>
                        <UserLayout>
                          <></>
                        </UserLayout>
                      ) : (
                        <Routes>
                          <Route
                            path="/"
                            element={
                              <AppLayout>
                                {/* <ProtectedRoute isSignedIn={isAuthenticated}> */}
                                <Outlet />
                                {/* </ProtectedRoute> */}
                              </AppLayout>
                            }
                          >
                            {/* <Route path="/" element={<Navigate to="home" replace />} /> */}
                            <Route path='login' element={<Login />} />
                            <Route path='error' element={<WizrNoAccessScreen />} />
                            <Route path='cookie-policy' element={<CookiePolicy />} />

                            <Route path='' element={
                              <ProtectedRoute isSignedIn={isAuthenticated}>
                                <DashboardPage />
                              </ProtectedRoute>
                            } />
                            <Route path='forgot-password' element={<ForgotPassword />} />
                            <Route path='change-password' element={<ChangePassword />} />
                            <Route path='extension' element={<ExtensionRedirectScreen />} />
                            <Route path='extension/login' element={<ExtensionRedirectScreen />} />
                            <Route path='wizrsdlc/login' element={<SdlcRedirectScreen />} />
                            <Route path='wizrsdlc' element={<SdlcRedirectScreen />} />
                            
                            <Route
                              path='home'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <DashboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='folder'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Folders />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='folder/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Files />
                                  {/* <KnowledgeBase/> */}

                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='notes'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <></>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='prompt'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Prompt />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='prompt-view'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <PromptView />
                                </ProtectedRoute>
                              }
                            />
                            <Route path="/settings" element={<Navigate to="/settings/profile" />} />
                            <Route
                              path='settings/:tab'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Settings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='integrations'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewIntegrationsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="langfuse/datasets"
                              element={
                                <LangfuseRedirectPage targetPath='datasets' />
                              }
                            />
                            <Route
                              path='app-designer'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppDesigner />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='app-designer/tool/:id?/:appId?/:action?'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppDesignerTool />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='workflow/configure'
                              element={ 
                                <WorkflowConfigure />
                              }
                            />
                            <Route
                              path='prompt-history/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <History />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='app-history/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='app-evals-history/:id'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppEvalsHistory />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='appshare/:id/:token'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                <AppShare />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='bulkrun'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <BulkRun />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='docview/:id'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                <DocView />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='docview'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                <DocView />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='tempEditor'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <TempEditor />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='appInsights/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppInsights />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='runhistory/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <RunHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='home'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <DashboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='enduserapp'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <EnterpriseContent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='euserapplisting'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <EndUserAppListing />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='euserhistory'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <EndUserHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='bulkrunlist/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <BulkRunList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='tenantdetails'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <TenantDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='userdetails'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <UserDetail />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CxSettings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/:type'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CxSettings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/autosolve'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewAutoSolve />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/agentassist'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewAgentAssist />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/autotag'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewAutoTag />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/widgets'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewToolsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='cxsettings/:type/new'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <NewFeatureScreen />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='controlroom/:selectedTab'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ControlRoom />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='past_tickets'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  {/* <NewFeatureScreen /> */}
                                  <PastTickets />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='past_tickets/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Tickets />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='faq/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Tickets />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='faq'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Faq />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='featuregroup/:title'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <GroupListing />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='featuregroup/:title/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <DataListingGroup />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='company'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Company />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path='featuretest'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <FeatureTest />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autotag/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoTag />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='agentassist/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AgentAssist />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolve/:id/:botId?'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoSolve />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolve/configure'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ConfigureAutosolve />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='endpoint'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <EndPoint />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path='userroles'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CreateRoles />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path='product'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <UpdateProduct />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path='chatbot/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ChatBot />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolvechatbot/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoSolveChatBot />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='chatbotlisting'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ChatBotListing />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='widgets/:type'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <WidgetCreation />
                                </ProtectedRoute>
                              }
                            />
                            {/* <Route
                              path='createAgent/:type/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CreateAgent />
                                </ProtectedRoute>
                              }
                            /> */}
                            <Route
                              path='aiAgentInsights'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AIAgentInsights />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='agentAssistInsights'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AgentAssistInsights />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='seekerInsights'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <SeekerInsights />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='usageInsights'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <UsageInsightsDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="langfuse/traces"
                              element={
                                <LangfuseRedirectPage targetPath='traces' />
                              }
                            />
                            {/* <Route
                          path='webFormatLarge/:id'
                          element={
                            <WebFormatLargePreview />
                          }
                        /> */}

                            <Route
                              path='testEmbed'
                              element={
                                <TestEmbed />
                              }
                            />

                            <Route
                              path='agenttag/:id'
                              element={
                                <AgentTag />
                              }
                            />

                            {/* <Route path="*" element={<Navigate to="/home" replace />} /> */}
                            <Route
                              path='widgetHistory'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <WidgetHistory />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path='chatbot/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ChatBot />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolvechatbot/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoSolveChatBot />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='chatbotlisting'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <ChatBotListing />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='widgets/:type'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <WidgetCreation />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='createAgent/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CreateAgent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='createAgent/:type/:id/:appId'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CreateAgent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='controlroom/customer-intents-and-sentiments'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                <CustomerIntentsAndSentiments />
                                // </ProtectedRoute>
                              }
                            />


                            <Route
                              path='customerInteractions'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CustomerInteractions />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='agentassist'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <CustomerInteractionsAgentAssist />
                                </ProtectedRoute>
                              }
                            />
                            {/* <Route
                          path='webFormatLarge/:id'
                          element={
                            <WebFormatLargePreview />
                          }
                        /> */}
                            <Route
                              path='appInsights'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AppInsightsWhole />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='testEmbed/:id/:botName'
                              element={
                                <TestEmbed />
                              }
                            />
                            <Route
                              path='help'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <HelpAndSupport />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='agenttag/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AgentTag />
                                </ProtectedRoute>

                              }
                            />
                            <Route
                              path='webChatSmallPreview/:id/:botName'
                              element={
                                <WebChatSmallPreview />
                              }
                            />
                            <Route
                              path='testEmbed/:id/:botName'
                              element={
                                <TestEmbed />
                              }
                            />
                            <Route
                              path='/sharepoint'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <SharePointPicker />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='Scheduler'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Scheduler />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='scheduler/history/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <SchedulerHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='myworkspace'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <EnterpriseHub />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='liveagent'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <LiveAgent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolvetesthistory/:id'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoSolveTestHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolvetesthistory/:id/detailed-logs/:conversationId'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <DetailedLogs />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path='autosolveevalshistory/:id'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <AutoSolveEvalsHistory />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='trustforte-list'
                              element={
                                // <ProtectedRoute isSignedIn={isAuthenticated}>
                                <TrustforteList />
                                // </ProtectedRoute>
                              }
                            />
                            <Route
                              path='notifications'
                              element={
                                <ProtectedRoute isSignedIn={isAuthenticated}>
                                  <Notifications />
                                </ProtectedRoute>
                              }
                            />
                          </Route>
                        </Routes>
                      )}
                    </Suspense>
                  </ReactFlowProvider>
                </WizrNotificationProvider>
              </WizrModalProvider>
            </PersistGate>
          </AuthProvider>
        </Provider>
      </ConfigProvider>
    </WizrFlexLayout>
  )
}

export default App